/**
 * APX Advisors — Terms of Service Page
 * Publicly accessible at /terms (no auth required)
 * Uses inline styles to be fully independent of app theme/CSS variables
 */
import { useEffect } from "react";

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    color: "#111827",
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
  } as React.CSSProperties,
  header: {
    backgroundColor: "#0f172a",
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  } as React.CSSProperties,
  headerLogo: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    backgroundColor: "#b8902a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "14px",
    flexShrink: 0,
  } as React.CSSProperties,
  headerTitle: {
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 600,
    margin: 0,
  } as React.CSSProperties,
  headerSub: {
    color: "#94a3b8",
    fontSize: "12px",
    margin: 0,
  } as React.CSSProperties,
  container: {
    maxWidth: "760px",
    margin: "0 auto",
    padding: "48px 24px 80px",
  } as React.CSSProperties,
  h1: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 6px",
  } as React.CSSProperties,
  lastUpdated: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "0 0 40px",
  } as React.CSSProperties,
  section: {
    marginBottom: "36px",
  } as React.CSSProperties,
  h2: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 10px",
    paddingBottom: "6px",
    borderBottom: "1px solid #e5e7eb",
  } as React.CSSProperties,
  p: {
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#374151",
    margin: "0 0 10px",
  } as React.CSSProperties,
  ul: {
    paddingLeft: "20px",
    margin: "8px 0",
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#374151",
  } as React.CSSProperties,
  li: {
    marginBottom: "4px",
  } as React.CSSProperties,
  link: {
    color: "#1d4ed8",
    textDecoration: "underline",
  } as React.CSSProperties,
  strong: {
    fontWeight: 600,
    color: "#111827",
  } as React.CSSProperties,
  footer: {
    borderTop: "1px solid #e5e7eb",
    marginTop: "48px",
    paddingTop: "24px",
    fontSize: "13px",
    color: "#9ca3af",
    textAlign: "center",
  } as React.CSSProperties,
};

export default function Terms() {
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLogo}>A</div>
        <div>
          <p style={styles.headerTitle}>APX Advisors LLC</p>
          <p style={styles.headerSub}>apxcfo.com · (310) 626-2670</p>
        </div>
      </div>

      {/* Content */}
      <div style={styles.container}>
        <h1 style={styles.h1}>Terms of Service</h1>
        <p style={styles.lastUpdated}>Last updated: May 19, 2026</p>

        {/* 1. Acceptance */}
        <div style={styles.section}>
          <h2 style={styles.h2}>1. Acceptance of Terms</h2>
          <p style={styles.p}>
            By accessing or using the APX Advisors LLC ("APX," "we," "our," or "us") internal
            operations platform at apxsystem.io, or by receiving communications from APX including
            SMS messages, email, or other digital services, you agree to be bound by these Terms of
            Service. If you do not agree to these terms, do not use our services.
          </p>
        </div>

        {/* 2. Services */}
        <div style={styles.section}>
          <h2 style={styles.h2}>2. Description of Services</h2>
          <p style={styles.p}>
            APX Advisors LLC provides bookkeeping, accounting, tax preparation, and financial
            advisory services to small and mid-size businesses. Our internal platform (apxsystem.io)
            is used by APX staff and authorized clients to manage engagements, share documents,
            communicate, and access financial reports. Access is granted by invitation only.
          </p>
        </div>

        {/* 3. Authorized Use */}
        <div style={styles.section}>
          <h2 style={styles.h2}>3. Authorized Use</h2>
          <p style={styles.p}>
            Access to the APX platform is restricted to authorized users only. You agree to:
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}>Use the platform solely for legitimate business purposes related to your engagement with APX</li>
            <li style={styles.li}>Keep your login credentials confidential and not share them with any third party</li>
            <li style={styles.li}>Notify APX immediately at <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a> if you suspect unauthorized access to your account</li>
            <li style={styles.li}>Not attempt to access data or systems beyond your authorized scope</li>
            <li style={styles.li}>Not use the platform for any unlawful purpose or in violation of any applicable regulations</li>
          </ul>
        </div>

        {/* 4. SMS Communications */}
        <div style={styles.section}>
          <h2 style={styles.h2}>4. SMS Communications</h2>
          <p style={styles.p}>
            By providing your mobile phone number and consenting to receive SMS messages from APX,
            you agree to receive transactional text messages related to your engagement, including
            account updates, document requests, appointment reminders, and invoice notices. Standard
            message and data rates may apply.
          </p>
          <p style={styles.p}>
            You may opt out at any time by replying <strong style={styles.strong}>STOP</strong> to
            any message. For help, reply <strong style={styles.strong}>HELP</strong> or contact us
            at <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a> or{" "}
            (310) 626-2670. For full details, see our{" "}
            <a href="/privacy-policy" style={styles.link}>Privacy Policy</a>.
          </p>
        </div>

        {/* 5. Confidentiality */}
        <div style={styles.section}>
          <h2 style={styles.h2}>5. Confidentiality</h2>
          <p style={styles.p}>
            All financial data, reports, communications, and documents shared through the APX
            platform are confidential. You agree not to disclose, reproduce, or distribute any
            information obtained through the platform to any third party without APX's prior written
            consent, except as required by law.
          </p>
        </div>

        {/* 6. Intellectual Property */}
        <div style={styles.section}>
          <h2 style={styles.h2}>6. Intellectual Property</h2>
          <p style={styles.p}>
            All content, software, design, and materials on the APX platform are the exclusive
            property of APX Advisors LLC or its licensors. You may not copy, modify, distribute,
            or create derivative works from any platform content without express written permission
            from APX.
          </p>
        </div>

        {/* 7. Disclaimer of Warranties */}
        <div style={styles.section}>
          <h2 style={styles.h2}>7. Disclaimer of Warranties</h2>
          <p style={styles.p}>
            The APX platform is provided "as is" and "as available" without warranties of any kind,
            express or implied. APX does not warrant that the platform will be uninterrupted,
            error-free, or free of viruses or other harmful components. Your use of the platform is
            at your sole risk.
          </p>
        </div>

        {/* 8. Limitation of Liability */}
        <div style={styles.section}>
          <h2 style={styles.h2}>8. Limitation of Liability</h2>
          <p style={styles.p}>
            To the fullest extent permitted by applicable law, APX Advisors LLC shall not be liable
            for any indirect, incidental, special, consequential, or punitive damages arising from
            your use of or inability to use the platform or services, even if APX has been advised
            of the possibility of such damages. APX's total liability to you for any claim shall not
            exceed the fees paid by you to APX in the three months preceding the claim.
          </p>
        </div>

        {/* 9. Governing Law */}
        <div style={styles.section}>
          <h2 style={styles.h2}>9. Governing Law</h2>
          <p style={styles.p}>
            These Terms of Service shall be governed by and construed in accordance with the laws
            of the State of California, without regard to its conflict of law provisions. Any
            disputes arising under these terms shall be resolved exclusively in the state or federal
            courts located in Los Angeles County, California.
          </p>
        </div>

        {/* 10. Changes to Terms */}
        <div style={styles.section}>
          <h2 style={styles.h2}>10. Changes to These Terms</h2>
          <p style={styles.p}>
            APX reserves the right to modify these Terms of Service at any time. We will notify
            authorized users of material changes via email or through the platform. Your continued
            use of the platform after such changes constitutes your acceptance of the updated terms.
          </p>
        </div>

        {/* 11. Contact */}
        <div style={styles.section}>
          <h2 style={styles.h2}>11. Contact Us</h2>
          <p style={styles.p}>
            APX Advisors LLC<br />
            1225 W 190th St, Los Angeles, CA 90248<br />
            Phone: <a href="tel:+13106262670" style={styles.link}>(310) 626-2670</a><br />
            Email: <a href="mailto:privacy@apxcfo.com" style={styles.link}>privacy@apxcfo.com</a><br />
            Website: <a href="https://apxcfo.com" style={styles.link} target="_blank" rel="noopener noreferrer">apxcfo.com</a>
          </p>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} APX Advisors LLC · All rights reserved ·{" "}
            <a href="/privacy-policy" style={{ ...styles.link, color: "#9ca3af" }}>Privacy Policy</a>
            {" · "}
            <a href="mailto:privacy@apxcfo.com" style={{ ...styles.link, color: "#9ca3af" }}>privacy@apxcfo.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
